import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { faCheckSquare } from '@fortawesome/pro-regular-svg-icons/faCheckSquare'

export default function EmrReleaseScroller({
  consent,
}: {
  consent: {
    consentAddressCity: string | null
    consentAddressCountry: string | null
    consentAddressLine1: string | null
    consentAddressState: string | null
    consentAddressZip: string | null
    consentDob: string | null
    consentLegalName: string | null
    consentSignature: string | null // data:image/png;base64,iVBORw0KGgoAA... etc
    consentSSNSuffix: string | null // last 4 digits of SSN
  }
}) {
  const address = [
    consent.consentAddressLine1,
    consent.consentAddressCity,
    consent.consentAddressState,
    consent.consentAddressZip,
    consent.consentAddressCountry,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <div className='grid grid-cols-1 gap-2'>
      <Text styleName='h4'>Complete Your Medical Record Release</Text>
      <div className='relative rounded-lg border border-neutral200'>
        <div className='absolute bottom-0 left-0 z-10 h-16 w-full rounded-b-lg bg-gradient-to-t from-white to-transparent' />
        <div className='show-scrollbar grid max-h-48 grid-cols-1 gap-2 overflow-y-scroll p-2 md:max-h-64 md:p-4'>
          <Text
            element='p'
            styleName='p-tiny'
            value='I hereby authorize the release of records from:'
          />

          <div className='mb-0.5'>
            <Text className='inline' styleName='p-tiny'>
              <strong>Patient Legal Name: </strong>
              {consent.consentLegalName ?? '******* *******'},{' '}
            </Text>
            <Text className='inline' styleName='p-tiny'>
              <strong>Date of Birth: </strong>
              {consent.consentDob ?? '**/**/****'},{' '}
            </Text>
            <Text className='inline' styleName='p-tiny'>
              <strong>Address: </strong>
              {address ?? '*******'},{' '}
            </Text>
          </div>

          <div className='mb-0.5'>
            <Text className='inline' styleName='p-tiny'>
              <strong>Recipient Organization: </strong>
              Power Life Sciences Inc.,{' '}
            </Text>
            <Text className='inline' styleName='p-tiny'>
              <strong>Address: </strong>
              1045 Sansome St, Suite 321, San Francisco, CA 94111,{' '}
            </Text>
            <Text className='inline' styleName='p-tiny'>
              <strong>Email: </strong>
              records@withpower.com,{' '}
            </Text>
            <Text className='inline' styleName='p-tiny'>
              <strong>Fax: </strong>
              (866) 854 2184,{' '}
            </Text>
          </div>

          <div className='mb-0.5'>
            <Text element='label' styleName='p-tiny'>
              <strong>Purpose of disclosure: </strong>
              <strong className='underline'>Continuation of care </strong> This
              release is my request in my capacity as a Patient and will be used
              at my direction or the direction of my legal representative.
            </Text>
          </div>

          <div className='mb-0.5'>
            <Text
              element='label'
              styleName='p-tiny-strong'
              value='Records I consent to sharing: Full Medical Record from All Providers including:'
            />
            <div className='grid grid-cols-1 gap-2 md:grid-cols-3'>
              <div className='flex items-center gap-2'>
                <Icon className='bg-success300' icon={faCheckSquare} />
                <Text
                  element='p'
                  styleName='p-tiny'
                  value='All Medical Notes &amp; Reports'
                />
              </div>
              <div className='flex items-center gap-2'>
                <Icon className='bg-success300' icon={faCheckSquare} />
                <Text
                  element='p'
                  styleName='p-tiny'
                  value='Pathology Reports'
                />
              </div>
              <div className='flex items-center gap-2'>
                <Icon className='bg-success300' icon={faCheckSquare} />
                <Text
                  element='p'
                  styleName='p-tiny'
                  value='Radiology Reports'
                />
              </div>
              <div className='flex items-center gap-2'>
                <Icon className='bg-success300' icon={faCheckSquare} />
                <Text element='p' styleName='p-tiny' value='Genetic Testing' />
              </div>
              <div className='flex items-center gap-2'>
                <Icon className='bg-success300' icon={faCheckSquare} />
                <Text
                  element='p'
                  styleName='p-tiny'
                  value='Operative Reports'
                />
              </div>
              <div className='flex items-center gap-2'>
                <Icon className='bg-success300' icon={faCheckSquare} />
                <Text
                  element='p'
                  styleName='p-tiny'
                  value='Discharge Summaries'
                />
              </div>
              <div className='flex items-center gap-2'>
                <Icon className='bg-success300' icon={faCheckSquare} />
                <Text element='p' styleName='p-tiny' value='Provider Notes' />
              </div>
              <div className='flex items-center gap-2'>
                <Icon className='bg-success300' icon={faCheckSquare} />
                <Text element='p' styleName='p-tiny' value='Lab Reports' />
              </div>
              <div className='flex items-center gap-2'>
                <Icon className='bg-success300' icon={faCheckSquare} />
                <Text
                  element='p'
                  styleName='p-tiny'
                  value='Mental Health (psychiatric) Reports'
                />
              </div>
              <div className='flex items-center gap-2'>
                <Icon className='bg-success300' icon={faCheckSquare} />
                <Text
                  element='p'
                  styleName='p-tiny'
                  value='Drugs/Alcohol Reports'
                />
              </div>
            </div>
          </div>

          <div className='mb-0.5'>
            <Text styleName='p-tiny'>
              <strong>When will this authorization expire: </strong>
              This authorization may be revoked by the Patient at any time by
              giving oral or written notice.
            </Text>
          </div>

          <div className='mb-0.5 flex flex-col gap-1'>
            <Text styleName='p-tiny'>
              <strong>Authorization &amp; Signature: </strong>I authorize the
              release of medical information as requested above. I understand
              that the information that I authorize to be disclosed is protected
              by federal confidentiality rules (including 42 C.F.R. Part 2 and
              HIPAA). I understand that certain information is protected by
              federal and state law, which prohibit further disclosure of this
              information unless further disclosure is expressly permitted by my
              written consent.
            </Text>
            <Text styleName='p-tiny'>
              I understand that at any time I may request a copy of this
              authorization in writing from Power which is my authorized legal
              representative. I have voluntarily agreed to Power acting as my
              agent for the purpose of collecting medical records, and I
              expressly authorize Power to receive all medical records covered
              by this request in Power&apos;s capacity as the agent of my
              authorized legal representative. I have been informed of and
              understand the purpose for disclosing this Personal Health
              Information to Power. I have read and agree to Power&apos;s
              Privacy Policy, available at
              https://www.withpower.com/terms-of-service.
            </Text>
            <Text styleName='p-tiny'>
              I understand that I have the right to revoke this authorization at
              anytime by submitting a written revocation except to the extent
              the provider has disclosed Personal Health Information pursuant to
              this authorization. I understand that my treatment, payment,
              enrollment, or eligibility for benefits will not be conditioned on
              whether I sign this authorization.
            </Text>
            <Text styleName='p-tiny'>
              I understand that Power may use or disclose information it
              receives pursuant to this authorization as directed by my legal
              representative. I understand that this information may be
              redisclosed with my authorization, and once redisclosed may no
              longer be protected by federal or state laws. I understand that
              Power&apos;s legal obligations with respect to any such records
              may differ from the obligations that apply to my healthcare
              provider.
            </Text>
            <Text styleName='p-tiny'>
              By signing this authorization form, you authorize the use or
              disclosure of your protected health information as described
              above. This information may be redisclosed if the recipient(s)
              described on this form is not required by law to protect the
              privacy of the information, and such information is no longer
              protected by federal health information privacy regulations.
            </Text>
            <Text styleName='p-tiny'>
              You have a right to refuse to sign this authorization. Your health
              care, the payment for your health care, and your health care
              benefits will not be affected if you do not sign this form.
            </Text>
            <Text styleName='p-tiny'>
              You have a right to see and copy the information described on this
              authorization form in accordance with hospital policies. You also
              have a right to receive a copy of this form after you have signed
              it. If you sign this authorization, you will have the right to
              revoke it at any time, except to the extent that the hospital has
              already taken action based upon your authorization. To revoke this
              authorization, please write to the Privacy Office at the hospital.
            </Text>
          </div>

          <Text styleName='p-tiny'>
            <strong>Signature:</strong> I am the Patient, and I am of lawful age
            to request my records.
          </Text>
          <div className='mb-0.5 grid grid-cols-1 gap-2 md:grid-cols-3'>
            <div>
              <Text styleName='p-tiny'>
                <strong>Signature: </strong>
                --------------
              </Text>
            </div>
            <div>
              <Text styleName='p-tiny'>
                <strong>Patient Name: </strong>
                {consent.consentLegalName ?? '*******'}
              </Text>
            </div>
            <div>
              <Text styleName='p-tiny'>
                <strong>Date: </strong>
                {new Date().toLocaleDateString()}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
